const DEVICE_KEY = 'mcc_device_id';
const DEVICE_NAME_KEY = 'mcc_device_name';
const PENDING_KEY = 'mcc_pending_token';
const DEVICE_COOKIE_MAX_AGE = 400 * 24 * 3600;

function readBrowserCookie(name) {
  try {
    const raw = document.cookie
      .split(';')
      .map((p) => p.trim())
      .find((p) => p.startsWith(`${name}=`));
    if (!raw) return '';
    return decodeURIComponent(raw.slice(name.length + 1));
  } catch {
    return '';
  }
}

function fingerprintCookieDomain() {
  try {
    const h = window.location.hostname;
    if (h === 'paylegit.in' || h.endsWith('.paylegit.in')) return '; Domain=.paylegit.in';
    if (h === 'aitechsecure.com' || h.endsWith('.aitechsecure.com')) return '; Domain=.aitechsecure.com';
  } catch {
    /* ignore */
  }
  return '';
}

function writeBrowserCookie(name, value, maxAge = DEVICE_COOKIE_MAX_AGE) {
  try {
    const secure = window.location.protocol === 'https:' ? '; Secure' : '';
    const domain = fingerprintCookieDomain();
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAge}; SameSite=Lax${secure}${domain}`;
  } catch {
    /* ignore */
  }
}

export async function getFingerprintId() {
  const cached = readBrowserCookie('pl_fp_id');
  if (cached) return cached;
  let visitorId = '';
  try {
    if (!window.FingerprintJS) {
      await new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://openfpcdn.io/fingerprintjs/v4/iife.min.js';
        s.async = true;
        s.onload = () => resolve();
        s.onerror = () => reject(new Error('fingerprintjs'));
        document.head.appendChild(s);
      });
    }
    if (window.FingerprintJS) {
      const fp = await window.FingerprintJS.load();
      visitorId = (await fp.get()).visitorId;
    }
  } catch {
    /* ignore */
  }
  if (!visitorId) visitorId = getOrCreateDeviceId();
  writeBrowserCookie('pl_fp_id', visitorId);
  return visitorId;
}

export async function fetchDevicePolicy(email) {
  const fp = await getFingerprintId();
  const q = new URLSearchParams({ fingerprint_id: fp, device_id: fp });
  if (email) q.set('email', email);
  try {
    const r = await fetch(`/api/v1/auth/device-policy?${q}`, {
      credentials: 'include',
    });
    if (!r.ok) return { skip_location: false };
    return await r.json();
  } catch {
    return { skip_location: false };
  }
}

function newDeviceId() {
  try {
    if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
      return crypto.randomUUID();
    }
  } catch {
    /* ignore */
  }
  return `mcc-${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}`;
}

export function getOrCreateDeviceId() {
  let id = '';
  try {
    id = localStorage.getItem(DEVICE_KEY) || '';
  } catch {
    /* ignore */
  }
  if (!id) id = readBrowserCookie(DEVICE_KEY);
  if (!id) id = newDeviceId();
  try {
    localStorage.setItem(DEVICE_KEY, id);
  } catch {
    /* ignore */
  }
  writeBrowserCookie(DEVICE_KEY, id);
  return id;
}

export function getStoredDeviceName() {
  try {
    return localStorage.getItem(DEVICE_NAME_KEY) || '';
  } catch {
    return '';
  }
}

export function getPendingToken() {
  try {
    return sessionStorage.getItem(PENDING_KEY) || '';
  } catch {
    return '';
  }
}

export function setPendingToken(token) {
  try {
    if (token) sessionStorage.setItem(PENDING_KEY, token);
    else sessionStorage.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

export async function detectMachineName() {
  const nav = navigator;
  try {
    if (nav.userAgentData?.getHighEntropyValues) {
      const high = await nav.userAgentData.getHighEntropyValues(['platform', 'platformVersion', 'model', 'architecture']);
      const parts = [high.platform, high.platformVersion, high.model, high.architecture].filter(Boolean);
      if (parts.length) return parts.join(' ');
    }
    if (nav.userAgentData?.platform) return nav.userAgentData.platform;
  } catch {
    /* ignore */
  }
  return navigator.platform || 'Browser';
}

export async function getLocationPermission() {
  try {
    if (!navigator.permissions?.query) return 'unknown';
    const status = await navigator.permissions.query({ name: 'geolocation' });
    if (status.state === 'granted' || status.state === 'denied' || status.state === 'prompt') {
      return status.state;
    }
  } catch {
    /* Safari */
  }
  return 'unknown';
}

function readPosition(options) {
  return new Promise((resolve, reject) => {
    navigator.geolocation.getCurrentPosition(resolve, reject, options);
  });
}

export async function requestLocation() {
  if (!navigator.geolocation) {
    return { ok: false, granted: false, denied: true, error: 'Geolocation is not supported' };
  }
  const perm = await getLocationPermission();
  if (perm === 'granted') {
    try {
      const pos = await readPosition({ enableHighAccuracy: false, timeout: 60000, maximumAge: 60000 });
      return {
        ok: true,
        granted: true,
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
      };
    } catch {
      return { ok: false, granted: false, error: 'Could not read GPS. Location access is mandatory.' };
    }
  }
  try {
    const pos = await readPosition({ enableHighAccuracy: true, timeout: 60000, maximumAge: 0 });
    return {
      ok: true,
      granted: true,
      latitude: pos.coords.latitude,
      longitude: pos.coords.longitude,
    };
  } catch (err) {
    if (err?.code === 1 || perm === 'denied') {
      return { ok: false, granted: false, denied: true, error: 'Location permission denied' };
    }
    return { ok: false, granted: false, denied: false, error: err?.message || 'Could not read location' };
  }
}

export async function fetchPublicIPs() {
  const out = { ipv4: '', ipv6: '' };
  const timedJson = async (url, ms) => {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), ms);
    try {
      const r = await fetch(url, { signal: ctrl.signal });
      return await r.json();
    } finally {
      clearTimeout(t);
    }
  };
  try {
    const local = await timedJson('/api/v1/auth/netinfo', 1500);
    out.ipv4 = local.ipv4 || '';
    out.ipv6 = local.ipv6 || '';
  } catch {
    /* ignore */
  }
  if (!out.ipv4) {
    try {
      const j = await timedJson('https://api.ipify.org?format=json', 1500);
      if (typeof j.ip === 'string' && j.ip.includes('.')) out.ipv4 = j.ip;
    } catch {
      /* ignore */
    }
  }
  return out;
}

export async function collectDeviceContext(location, personalName) {
  const [machine_name, ips] = await Promise.all([detectMachineName(), fetchPublicIPs()]);
  const granted = location?.granted === true || location?.ok === true;
  const ctx = {
    device_id: await getFingerprintId(),
    machine_name,
    device_name: personalName || getStoredDeviceName() || `${machine_name} browser`,
    ipv4: ips.ipv4,
    ipv6: ips.ipv6,
    location_granted: granted,
  };
  if (location?.latitude != null && location?.longitude != null) {
    ctx.latitude = location.latitude;
    ctx.longitude = location.longitude;
  }
  writeBrowserCookie(DEVICE_KEY, ctx.device_id);
  return ctx;
}
